import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { categories } from '../utils/data';
import { Category } from '../types';
import { useChatAI } from '../hooks/useChatAI';
import { Message } from '../types/chat';
import MessageRenderer from './chat/MessageRenderer';

const DirectChatHero: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: '¡Bienvenido a BuenAire! 🏗️\n\nSoy tu asistente virtual especializado en construcción. Estoy aquí para ayudarte a encontrar todo lo que necesitás para tu proyecto.\n\n💡 **¿Cómo puedo ayudarte hoy?**\n- Presupuestos y costos de materiales\n- Recomendaciones de proveedores\n- Planificación de proyectos\n- Selección de materiales de calidad\n- Cronogramas de construcción\n\n¡Escribí tu consulta y te ayudo a hacer realidad tu proyecto! 😊',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { sendMessage, isLoading } = useChatAI();

  // Auto-scroll al último mensaje
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Disparar evento para mostrar popup de bienvenida después de la animación del título
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('DirectChatHero: Disparando evento showWelcomePopup después de animación del título');
      const event = new CustomEvent('showWelcomePopup');
      document.dispatchEvent(event);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Agregar búsqueda como mensaje al chat
      const searchMessage: Message = {
        id: Date.now().toString(),
        text: `Buscar: ${searchQuery}`,
        sender: 'user',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, searchMessage]);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() && !isLoading) {
      const userMessage: Message = {
        id: Date.now().toString(),
        text: newMessage,
        sender: 'user',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, userMessage]);
      const currentMessage = newMessage;
      setNewMessage('');
      
      try {
        const response = await sendMessage(currentMessage, messages);
        
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: response.message,
          sender: 'bot',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botMessage]);
      } catch (error) {
        console.error('Error al procesar mensaje:', error);
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Ups, parece que tuve un problema técnico. ¿Podés intentar de nuevo? 😅',
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="direct-chat-hero">
      <div className="uk-position-relative uk-light" style={{ height: '100vh', overflow: 'hidden' }}>
        <div className="direct-chat-layout">
          {/* Sección Izquierda - Chat Directo */}
          <div className="chat-main-section">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="chat-container"
            >
              {/* Header del Chat */}
              <div className="chat-header">
                <div className="chat-header-info">
                  <div className="chat-avatar">
                    <img 
                      src="/assets/img/ai-avatar-buenaire.svg" 
                      alt="Asistente BuenAire"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="avatar-fallback hidden">
                      <span>B</span>
                    </div>
                  </div>
                  <div className="chat-header-text">
                    <h2>¡Bienvenido a BuenAire!</h2>
                    <p>Tu asistente virtual de construcción</p>
                  </div>
                </div>
                <div className="chat-actions">
                  <button 
                    className="action-btn search-btn"
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                    title="Búsqueda rápida"
                  >
                    <i className="fas fa-search"></i>
                  </button>
                  <button 
                    className="action-btn categories-btn"
                    onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
                    title="Categorías"
                  >
                    <i className="fas fa-th-large"></i>
                  </button>
                </div>
              </div>

              {/* Área de Mensajes */}
              <div className="messages-area" ref={messagesEndRef}>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    className={`message-item ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
                  >
                    {message.sender === 'bot' && (
                      <div className="message-avatar">
                        <img 
                          src="/assets/img/ai-avatar-buenaire.svg" 
                          alt="Asistente BuenAire"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="avatar-fallback-small hidden">
                          <span>B</span>
                        </div>
                      </div>
                    )}
                    <div className={`message-content ${message.sender === 'user' ? 'user-content' : 'bot-content'}`}>
                      <MessageRenderer message={message} />
                      <div className="message-time">
                        {message.timestamp.toLocaleTimeString('es-ES', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="message-item bot-message"
                  >
                    <div className="message-avatar">
                      <img 
                        src="/assets/img/ai-avatar-buenaire.svg" 
                        alt="Asistente BuenAire"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="avatar-fallback-small hidden">
                        <span>B</span>
                      </div>
                    </div>
                    <div className="message-content bot-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Input de Mensaje */}
              <div className="chat-input-section">
                <div className="input-container">
                  <input
                    type="text"
                    placeholder="Escribí tu consulta aquí..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="message-input"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || isLoading}
                    className="send-button"
                  >
                    {isLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-paper-plane"></i>
                    )}
                  </button>
                </div>
                <div className="input-suggestions">
                  <span className="suggestion" onClick={() => setNewMessage("Necesito presupuesto para una casa")}>
                    💰 "Necesito presupuesto para una casa"
                  </span>
                  <span className="suggestion" onClick={() => setNewMessage("¿Qué materiales recomendas?")}>
                    🔨 "¿Qué materiales recomendas?"
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sección Derecha - Búsqueda y Categorías */}
          <div className="tools-section">
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="tools-container"
            >
              {/* Búsqueda Rápida */}
              <div className="search-widget">
                <div className="widget-header">
                  <i className="fas fa-search"></i>
                  <h3>Búsqueda Rápida</h3>
                </div>
                <form onSubmit={handleSearch} className="search-form">
                  <div className="search-input-group">
                    <input 
                      type="text" 
                      placeholder="Buscar servicios o productos..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    <button type="submit" className="search-btn">
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </form>
              </div>

              {/* Categorías Dropdown */}
              <div className="categories-widget">
                <div className="widget-header">
                  <i className="fas fa-th-large"></i>
                  <h3>Categorías</h3>
                </div>
                <div className="categories-dropdown">
                  <button 
                    className="dropdown-toggle"
                    onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
                  >
                    <span>Ver todas las categorías</span>
                    <i className={`fas fa-chevron-${isCategoriesOpen ? 'up' : 'down'}`}></i>
                  </button>
                  
                  <AnimatePresence>
                    {isCategoriesOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="dropdown-content"
                      >
                        <div className="categories-grid">
                          {categories.map((category: Category) => (
                            <Link 
                              key={category.id}
                              to={`/${category.id}`} 
                              className="category-item"
                              onClick={() => setIsCategoriesOpen(false)}
                            >
                              <i className={category.icon}></i>
                              <span>{category.name}</span>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Información Adicional */}
              <div className="info-widget">
                <div className="widget-header">
                  <i className="fas fa-info-circle"></i>
                  <h3>Información</h3>
                </div>
                <div className="info-cards">
                  <div className="info-card">
                    <i className="fas fa-phone"></i>
                    <div>
                      <h4>Soporte 24/7</h4>
                      <p>+54 9 11 3310-2465</p>
                    </div>
                  </div>
                  
                  <div className="info-card">
                    <i className="fas fa-shield-alt"></i>
                    <div>
                      <h4>Proveedores Verificados</h4>
                      <p>Calidad garantizada</p>
                    </div>
                  </div>

                  <div className="info-card">
                    <i className="fas fa-rocket"></i>
                    <div>
                      <h4>Respuesta Rápida</h4>
                      <p>Asistencia inmediata</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectChatHero;
